import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { captureSidekickActingOrganizationScope } from '../api/actingOrganization';
import type {
  EnsuredSidekickConversation,
  EnsureSidekickConversation,
} from '../contexts/SidekickApiContext';
import {
  selectConversationId,
  selectConversationScopeVersion,
} from '../state/aiSidekickChatSlice';
import type { StateWithSidekickChat } from '../state/chatTypes';
import { useCreateConversation } from './useCreateConversation';

type CreateInFlight = {
  promise: Promise<EnsuredSidekickConversation>;
  scopeVersion: number;
};

/**
 * Package-owned default for {@link EnsureSidekickConversation}, backed
 * by the package's generated `ConversationService` client. Creates the
 * backend conversation the orchestrator needs before its first user turn.
 *
 * Hosts that already own conversation creation keep injecting their own
 * callback on the shell; this is the fallback for hosts that do not.
 *
 * @returns A callback resolving to the active conversation's id and creation timestamp.
 */
export const useDefaultEnsureConversation = (): EnsureSidekickConversation => {
  const conversationId = useSelector(selectConversationId);
  const conversationScopeVersion = useSelector(selectConversationScopeVersion);
  // Selected as a primitive rather than through `selectConversation` so
  // appending a message does not produce a new callback identity.
  const conversationCreatedAt = useSelector(
    (state: StateWithSidekickChat) =>
      state.aiSidekickChat.conversationMetadata?.createdAt ?? null
  );
  const createConversation = useCreateConversation();
  const createInFlightRef = useRef<CreateInFlight | null>(null);

  return useCallback(async () => {
    if (conversationId !== null && conversationCreatedAt !== null) {
      return { conversationId, createdAt: conversationCreatedAt };
    }
    if (createInFlightRef.current?.scopeVersion === conversationScopeVersion) {
      return createInFlightRef.current.promise;
    }

    const organizationScope = captureSidekickActingOrganizationScope();
    const promise = createConversation(organizationScope).finally(() => {
      if (createInFlightRef.current?.promise === promise) {
        createInFlightRef.current = null;
      }
    });
    createInFlightRef.current = {
      promise,
      scopeVersion: conversationScopeVersion,
    };
    return promise;
  }, [
    conversationId,
    conversationCreatedAt,
    conversationScopeVersion,
    createConversation,
  ]);
};
